import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const STATUSES = ["new", "contacted", "quoted", "scheduled", "closed"];

const statusCls = {
  new: "bg-leaf/15 text-leaf-hover",
  contacted: "bg-amber-100 text-amber-700",
  quoted: "bg-blue-100 text-blue-700",
  scheduled: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-500",
};

export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/leads", { params: filter ? { service_type: filter } : {} })
      .then((r) => setLeads(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(load, [filter]);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? data : l)));
      toast.success("Lead updated");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-leads-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <h2 className="font-display text-xl font-bold text-forest">Quote Requests ({leads.length})</h2>
        <select
          data-testid="leads-filter-select"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="rounded-xl border border-forest/15 bg-white px-4 py-2 text-sm"
        >
          <option value="">All services</option>
          <option value="residential">Residential</option>
          <option value="commercial">Commercial</option>
        </select>
      </div>
      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-leaf" />
      ) : leads.length === 0 ? (
        <p className="text-slate-500 text-sm" data-testid="leads-empty">No leads yet. New quote requests will appear here.</p>
      ) : (
        <div className="space-y-3">
          {leads.map((lead) => (
            <div key={lead.id} className="rounded-2xl bg-white border border-forest/10 p-5" data-testid={`lead-card-${lead.id}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div>
                  <p className="font-display font-bold text-forest">{lead.full_name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {lead.service_type} · {lead.zip_code} · {format(new Date(lead.created_at), "MMM d, yyyy h:mm a")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <select
                    data-testid={`lead-status-${lead.id}`}
                    value={lead.status}
                    onChange={(e) => setStatus(lead.id, e.target.value)}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold border-0 ${statusCls[lead.status] || statusCls.new}`}
                  >
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <button onClick={() => setExpanded(expanded === lead.id ? null : lead.id)} data-testid={`lead-expand-${lead.id}`} className="p-2 text-slate-400 hover:text-forest" aria-label="Toggle details">
                    {expanded === lead.id ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </button>
                  <button onClick={() => remove(lead.id)} data-testid={`lead-delete-${lead.id}`} className="p-2 text-slate-400 hover:text-red-600" aria-label="Delete lead">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              {expanded === lead.id && (
                <div className="mt-4 pt-4 border-t border-forest/10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
                  <p><span className="text-slate-400">Email:</span> <a href={`mailto:${lead.email}`} className="text-leaf-hover font-medium">{lead.email}</a></p>
                  <p><span className="text-slate-400">Phone:</span> <a href={`tel:${lead.phone}`} className="text-leaf-hover font-medium">{lead.phone}</a></p>
                  <p><span className="text-slate-400">Property:</span> {lead.property_type || "-"}</p>
                  <p><span className="text-slate-400">Size:</span> {lead.square_footage || "-"}</p>
                  <p><span className="text-slate-400">Frequency:</span> {lead.frequency || "-"}</p>
                  <p><span className="text-slate-400">Start:</span> {lead.start_date || "-"}</p>
                  {lead.notes && <p className="sm:col-span-2"><span className="text-slate-400">Notes:</span> {lead.notes}</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
